import React, {
  Suspense,
  Fragment,
  lazy
} from 'react';
import {
  Switch,
  Redirect,
  Route
} from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import RedirectToV2, { RedirectNowToV2 } from 'src/components/RedirectToV2';
import { sunsetMode } from 'src/v2Sunset';
import type { V2SunsetPage } from 'src/v2Sunset';

/** Hand a v1 page over to v2 once it is retired — silently, or via the card. */
const sunsetOr = (page: V2SunsetPage, View: any) => (props: any) => {
  switch (sunsetMode(page)) {
    case 'immediate': return <RedirectNowToV2 page={page} />;
    case 'announce': return <RedirectToV2 page={page} />;
    default: return <View {...props} />;
  }
};

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes
                    ? renderRoutes(route.routes)
                    : <Component {...props} />}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: sunsetOr('login', lazy(() => import('src/views/auth/LoginView')))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/reset-password',
    component: sunsetOr('resetPassword', lazy(() => import('src/views/auth/ResetPasswordView')))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: sunsetOr('login', lazy(() => import('src/views/auth/LoginView')))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: sunsetOr('register', lazy(() => import('src/views/auth/RegisterView')))
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: sunsetOr('register', lazy(() => import('src/views/auth/RegisterView')))
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        // The search tabs. Ahead of `:tab` so in-app navigation to them is caught too.
        exact: true,
        path: ['/app/account', '/app/account/tracking', '/app/account/tracking-old'],
        component: sunsetOr('search', lazy(() => import('src/views/account/AccountView')))
      },
      {
        exact: true,
        path: '/app/account/:tab',
        component: lazy(() => import('src/views/account/AccountView'))
      },
      // {
      //   exact: true,
      //   path: '/app/tracking',
      //   component: lazy(() => import('src/views/TrackingNrs/TrackingNrsView'))
      // },
      {
        component: () => <Redirect to="/404" />
      }
    ],
  },
  {
    component: () => <Redirect to="/app/account/tracking" />
  }
];

export default routes;
